/**
 * OffertStatusBadge — the status chips for one offer (P2 Placement Slice 10, Tab 4a): the §10.2
 * lifecycle Badge + (only when the IDP state is NOT yet normalisiert) the Ch-6.5 IDP Badge + the E9
 * version chip («Aktuelle Version» / «Version n») + a «Verworfen» chip when discarded.
 */

import { Badge } from '../../components/ui/Badge';
import {
  OFFER_AKTUELL_LABEL,
  OFFER_IDP_STATUS,
  OFFER_IDP_STATUS_LABELS,
  OFFER_STATUS,
  OFFER_STATUS_LABELS,
  OFFER_VERSION_LABEL_PREFIX,
  OFFER_VERWORFEN_LABEL,
} from '@brokly/shared';
import type { OfferIdpStatus, OfferStatus } from '@brokly/shared';

type BadgeVariant = 'neutral' | 'outline' | 'success' | 'warning' | 'danger' | 'info';

const STATUS_VARIANT: Record<OfferStatus, BadgeVariant> = {
  [OFFER_STATUS.EINGEGANGEN]: 'neutral',
  [OFFER_STATUS.NORMALISIERT]: 'success',
  [OFFER_STATUS.IM_VERGLEICH]: 'info',
  [OFFER_STATUS.GEWAEHLT]: 'success',
  [OFFER_STATUS.NICHT_GEWAEHLT]: 'outline',
};

const IDP_VARIANT: Record<OfferIdpStatus, BadgeVariant> = {
  [OFFER_IDP_STATUS.AUSSTEHEND]: 'info',
  [OFFER_IDP_STATUS.KLASSIFIZIERT]: 'info',
  [OFFER_IDP_STATUS.NORMALISIERT]: 'success',
  [OFFER_IDP_STATUS.MANUELL_ZU_PRUEFEN]: 'warning',
  [OFFER_IDP_STATUS.KEINE_OFFERTE]: 'outline',
  [OFFER_IDP_STATUS.FEHLGESCHLAGEN]: 'danger',
};

export function OffertStatusBadge({
  status,
  idpStatus,
  isCurrent,
  versionNo,
  verworfen,
}: {
  status: OfferStatus;
  idpStatus: OfferIdpStatus;
  isCurrent: boolean;
  versionNo: number | null;
  verworfen: boolean;
}) {
  const showSpinner = idpStatus === OFFER_IDP_STATUS.AUSSTEHEND || idpStatus === OFFER_IDP_STATUS.KLASSIFIZIERT;
  return (
    <span className="inline-flex flex-wrap items-center gap-1" data-testid="offert-status-badge">
      <Badge variant={STATUS_VARIANT[status]}>{OFFER_STATUS_LABELS[status]}</Badge>
      {idpStatus !== OFFER_IDP_STATUS.NORMALISIERT && (
        <Badge variant={IDP_VARIANT[idpStatus]}>
          {showSpinner ? '⌛ ' : ''}
          {OFFER_IDP_STATUS_LABELS[idpStatus]}
        </Badge>
      )}
      {verworfen && <Badge variant="danger">{OFFER_VERWORFEN_LABEL}</Badge>}
      {!verworfen && isCurrent && <Badge variant="success">{OFFER_AKTUELL_LABEL}</Badge>}
      {!verworfen && !isCurrent && versionNo !== null && (
        <Badge variant="neutral">{`${OFFER_VERSION_LABEL_PREFIX}${versionNo}`}</Badge>
      )}
    </span>
  );
}
